import { ArrowRight, Brain, Code, Users, Zap, Award, Star, Clock, CheckCircle, ArrowDown, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { trackCourseClick } from '../components/GoogleAnalytics';
import '../styles/scrollAnimations.css';

const PlaywrightPathPage = () => {
  const courses = [
    {
      id: 1,
      title: "End to End Test Automation with Playwright (TS/C#/Java)",
      subtitle: "Foundation Course - Start Here",
      description: "Complete guide to end-to-end test automation using Playwright across multiple programming languages. Master web testing, mobile testing, and cross-browser automation.",
      longDescription: "Perfect starting point for Playwright automation across languages. Cover E2E testing fundamentals, cross-browser strategies, mobile testing, and modern web automation practices.",
      level: "Beginner",
      duration: "12 hours",
      students: "19,600+",
      rating: 4.7,
      price: "Enroll on Udemy",
      icon: Play,
      color: "from-green-500 to-emerald-500",
      borderColor: "border-green-200 hover:border-green-500/50",
      bgColor: "bg-green-50 hover:bg-green-100",
      skills: ["Playwright Basics", "Cross-browser Testing", "Mobile Testing", "TypeScript/C#/Java", "CI/CD Integration", "Web Automation"],
      udemy: "e2e-playwright",
      whatYoullLearn: [
        "Playwright automation fundamentals across languages",
        "Cross-browser and mobile testing strategies",
        "Modern web element handling and interaction",
        "Essential debugging techniques and best practices",
        "CI/CD integration for automated testing"
      ]
    },
    {
      id: 2,
      title: "Automation Framework Development with Playwright in C# .NET",
      subtitle: "Framework Development",
      description: "Master building robust, scalable test automation frameworks using Playwright with C# .NET. Learn advanced framework architecture, design patterns, reporting, and CI/CD integration.",
      longDescription: "Master advanced Playwright automation framework development with C# .NET. Cover framework architecture, design patterns, parallel execution, custom reporting, and enterprise integration.",
      level: "Intermediate",
      duration: "15 hours",
      students: "16,700+",
      rating: 4.8,
      price: "Enroll on Udemy",
      icon: Code,
      color: "from-blue-500 to-cyan-500",
      borderColor: "border-blue-200 hover:border-blue-500/50",
      bgColor: "bg-blue-50 hover:bg-blue-100",
      skills: ["Framework Architecture", "Design Patterns", "Page Object Model", "Parallel Execution", "Advanced Reporting", "C# .NET"],
      udemy: "framework-development-with-playwright-dotnet",
      whatYoullLearn: [
        "Design scalable Playwright framework architecture",
        "Implement advanced design patterns and best practices",
        "Create comprehensive reporting and logging systems",
        "Set up parallel test execution strategies",
        "Build maintainable and reusable test components"
      ]
    },
    {
      id: 3,
      title: "Advanced Automation Testing of Modern Apps with C# .NET",
      subtitle: "Advanced Testing Strategies",
      description: "Advanced techniques for testing modern applications including SPAs, PWAs, and cloud-native applications using Playwright. Master performance testing, security testing, and advanced automation strategies.",
      longDescription: "Advanced Playwright testing for modern applications. Cover SPA/PWA testing, performance optimization, security validation, cloud-native testing, and enterprise-grade automation strategies.",
      level: "Advanced",
      duration: "18 hours",
      students: "11,400+",
      rating: 4.9,
      price: "Enroll on Udemy",
      icon: Brain,
      color: "from-purple-500 to-pink-500",
      borderColor: "border-purple-200 hover:border-purple-500/50",
      bgColor: "bg-purple-50 hover:bg-purple-100",
      skills: ["SPA Testing", "PWA Testing", "Performance Testing", "Security Testing", "Cloud Testing", "Modern Apps"],
      udemy: "modern-dotnet-testing",
      whatYoullLearn: [
        "Test complex Single Page Applications effectively with Playwright",
        "Implement Progressive Web App testing strategies",
        "Perform automated performance and security testing",
        "Test cloud-native and distributed applications",
        "Handle advanced scenarios and edge cases"
      ]
    },
    {
      id: 4,
      title: "Event-Driven Microservice: Build and Test Apps with C# .NET",
      subtitle: "Expert Microservice Testing",
      description: "Expert-level course covering event-driven microservice architecture using Playwright for testing. Learn to build and test distributed systems, handle event sourcing, and implement comprehensive testing strategies for microservices.",
      longDescription: "Expert-level microservice testing with Playwright. Master event-driven architectures, distributed system testing, service mesh validation, and enterprise microservice strategies.",
      level: "Expert",
      duration: "20 hours",
      students: "6,500+",
      rating: 4.9,
      price: "Enroll on Udemy",
      icon: Zap,
      color: "from-orange-500 to-red-500",
      borderColor: "border-orange-200 hover:border-orange-500/50",
      bgColor: "bg-orange-50 hover:bg-orange-100",
      skills: ["Microservice Testing", "Event Sourcing", "Distributed Testing", "Integration Testing", "System Architecture", "C# .NET"],
      udemy: "testing-eda-microservices",
      whatYoullLearn: [
        "Design and test event-driven microservice systems with Playwright",
        "Implement comprehensive microservice testing strategies",
        "Handle event sourcing and distributed system testing",
        "Create end-to-end integration test suites",
        "Build resilient and scalable testing frameworks"
      ]
    }
  ];

  // Scroll animation hooks for each course
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const courseRefs = courses.map(() => useScrollAnimation({ threshold: 0.2, delay: 200 }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className={`relative bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-600 text-white overflow-hidden hero-scroll-fade ${heroVisible ? 'animate' : ''}`}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-indigo-900/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
                <Play className="w-12 h-12 text-white" />
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              Playwright <span className="text-blue-100">Mastery Path</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-blue-50 max-w-4xl mx-auto leading-relaxed mb-6 md:mb-8 px-2">
              Master Playwright automation from beginner to expert. Build enterprise frameworks, test modern applications, 
              and become a Playwright automation expert.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
              <div className="flex items-center gap-2 text-blue-100 text-sm sm:text-base">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                <span className="font-semibold">54,200+ students already enrolled</span>
              </div>
              <div className="flex items-center gap-2 text-blue-100 text-sm sm:text-base">
                <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>4 comprehensive courses</span>
              </div>
              <div className="flex items-center gap-2 text-blue-100 text-sm sm:text-base">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>65 hours total content</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Path */}
      <section className="py-12 md:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Path Header */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              Your <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Progressive Journey</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-2">
              Follow this carefully designed path from Playwright basics to microservice testing mastery
            </p>
          </div>

          {/* Course Path */}
          <div className="relative">
            {/* Central Path Line - Hidden on mobile */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-0.5 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 via-purple-500 via-indigo-500 to-orange-500 opacity-30"></div>

            {courses.map((course, index) => {
              const Icon = course.icon;
              const isEven = index % 2 === 0;
              const { ref, isVisible } = courseRefs[index];
              
              return (
                <div key={course.id} className="relative mb-8 md:mb-16 last:mb-0">
                  {/* Course Card */}
                  <div 
                    ref={ref}
                    className={`section-reveal ${isVisible ? 'animate' : ''} ${isEven ? 'md:pr-8 md:ml-0 md:text-right' : 'md:pl-8 md:ml-auto md:text-left'} md:w-1/2 w-full`}
                  >
                    <div 
                      className={`group relative bg-white rounded-xl md:rounded-2xl p-4 md:p-8 border-2 ${course.borderColor} transition-all duration-500 hover:shadow-2xl transform hover:scale-[1.02] cursor-pointer ${course.bgColor}`}
                      onClick={() => {
                        trackCourseClick(course.title, course.udemy);
                        window.open(`https://www.udemy.com/course/${course.udemy}/`, '_blank');
                      }}
                    >
                      
                      {/* Course Number Badge */}
                      <div className={`absolute ${isEven ? 'md:-right-4' : 'md:-left-4'} -top-3 md:-top-4 right-4 md:right-auto w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r ${course.color} rounded-full flex items-center justify-center text-white font-bold text-base md:text-lg shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        {index + 1}
                      </div>

                      {/* Course Header */}
                      <div className={`flex items-start gap-3 md:gap-6 mb-4 md:mb-6 ${isEven ? 'md:flex-row-reverse' : ''}`}>
                        <div className={`p-3 md:p-4 rounded-lg md:rounded-xl bg-gradient-to-r ${course.color} shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 md:gap-3 mb-2 flex-wrap">
                            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                              {course.level}
                            </span>
                            <div className="flex items-center gap-1 text-gray-500 text-sm">
                              <Clock className="w-3 h-3 md:w-4 md:h-4" />
                              <span>{course.duration}</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-500 text-sm">
                              <Users className="w-3 h-3 md:w-4 md:h-4" />
                              <span>{course.students}</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-500 text-sm">
                              <Star className="w-3 h-3 md:w-4 md:h-4 text-yellow-500" />
                              <span>{course.rating}</span>
                            </div>
                          </div>
                          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 leading-tight">
                            {course.title}
                          </h3>
                          <p className="text-xs md:text-sm font-medium text-blue-600 mb-2 md:mb-3 uppercase tracking-wide">
                            {course.subtitle}
                          </p>
                          <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                            {course.description}
                          </p>
                        </div>
                      </div>

                      {/* Skills Tags */}
                      <div className="flex flex-wrap gap-1 md:gap-2 mb-4 md:mb-6">
                        {course.skills.map((skill, skillIndex) => (
                          <span 
                            key={skillIndex}
                            className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full font-medium hover:bg-gray-200 transition-colors duration-200"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>

                      {/* What You'll Learn */}
                      <div className="mb-4 md:mb-6">
                        <h4 className="text-sm md:text-base font-semibold text-gray-900 mb-2 md:mb-3">What you'll learn:</h4>
                        <ul className="space-y-1 md:space-y-2">
                          {course.whatYoullLearn.slice(0, 3).map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-2 text-xs md:text-sm text-gray-600">
                              <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                          {course.whatYoullLearn.length > 3 && (
                            <li className="text-xs text-blue-600 font-medium ml-5 md:ml-6">
                              +{course.whatYoullLearn.length - 3} more learning objectives
                            </li>
                          )}
                        </ul>
                      </div>

                      {/* Action Button */}
                      <div className="flex items-center gap-3 pt-3 md:pt-4 border-t border-gray-100">
                        <button className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-medium text-sm md:text-base hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2 group-hover:shadow-lg">
                          {course.price}
                          <ArrowRight className="w-3 h-3 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform duration-200" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Path Connector */}
                  {index < courses.length - 1 && (
                    <div className="flex justify-center mt-6 md:mt-8 mb-2 md:mb-4">
                      <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-gray-200 to-gray-300 shadow-sm">
                        <ArrowDown className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-blue-50 to-indigo-100">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
            Ready to Start Your Playwright Journey?
          </h2>
          <p className="text-lg md:text-xl text-gray-600 mb-6 md:mb-8 max-w-2xl mx-auto">
            Join thousands of students who have transformed their careers with our comprehensive Playwright automation courses.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <button 
              onClick={() => {
                trackCourseClick('Playwright Path - Start Learning', courses[0].udemy);
                window.open(`https://www.udemy.com/course/${courses[0].udemy}/`, '_blank');
              }}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold text-base md:text-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Start Learning Now
            </button>
            <Link 
              to="/automation"
              className="border-2 border-gray-300 text-gray-700 px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold text-base md:text-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 transform hover:scale-105"
            >
              Browse All Courses
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PlaywrightPathPage;